// EID-139: the streaming scheduler — one residency manager per world.
//
// EIDOS has had no scheduler at all: every worldlayer fetches independently,
// with no shared budget, no priority and no cancellation on pan-away. That is
// fine until a pyramid starts switching levels (EID-137) and a tile traversal
// starts refining per frame (EID-144), at which point a single camera move can
// queue hundreds of requests that nothing orders or stops.
//
// Game engines treat streaming as a residency loop: an error metric decides
// the ideal, a scheduler moves data toward it under hard budgets, and the
// renderer degrades gracefully meanwhile. EID-137 supplies the metric,
// EID-138 the graceful degradation, this is the scheduler.
//
// Three properties are load-bearing:
//
//  1. PRIORITY IS TIME-TO-VISIBLE, not layer order and not request order. A
//     tile the user is looking at now, at the wrong level, outranks a
//     perfectly-chosen tile they might pan to.
//
//  2. THE BUDGET IS GLOBAL, not per layer. Ten layers each politely limiting
//     themselves to four concurrent fetches is forty concurrent fetches, which
//     is what the browser's connection pool experiences as a stall.
//
//  3. CANCELLATION IS THE POINT. Panning away from a region should stop its
//     fetches, not merely ignore them when they land — the bandwidth is the
//     scarce resource, not the callback.

/**
 * Ordered most to least urgent. The names are deliberately about WHEN the data
 * is needed rather than what it is, because that is the only ordering that
 * survives mixing zarr chunks, vector tiles and glTF assets in one queue.
 */
export const PRIORITIES = [
  /** On screen now, at the level the LOD policy actually asked for. */
  'visible',
  /** On screen now, but only a coarser level is resident — fills a gap. */
  'visible-fallback',
  /** Just outside the viewport, or the next timestep during playback. */
  'prefetch',
  /** Might be wanted eventually; first thing dropped under pressure. */
  'speculative',
] as const;

export type Priority = (typeof PRIORITIES)[number];

const RANK: Record<Priority, number> = PRIORITIES.reduce(
  (acc, p, i) => ({ ...acc, [p]: i }),
  {} as Record<Priority, number>,
);

export interface StreamRequest<T = unknown> {
  /**
   * Identity of the DATA, not of the request: `(layer, level, tile, timestep)`.
   * Two callers asking for the same thing share one fetch.
   */
  key: string;
  priority: Priority;
  /** Does the work. Must honour the signal, or cancellation only frees a slot. */
  run: (signal: AbortSignal) => Promise<T>;
  /** Optional grouping tag so a whole region/layer can be cancelled at once. */
  group?: string;
}

interface Entry {
  key: string;
  priority: Priority;
  group?: string;
  run: (signal: AbortSignal) => Promise<unknown>;
  resolve: (v: unknown) => void;
  reject: (e: unknown) => void;
  /** Monotonic, so equal priorities are FIFO rather than arbitrary. */
  seq: number;
  controller?: AbortController;
}

/** Thrown into a caller's promise when its request is cancelled. */
export class StreamCancelled extends Error {
  constructor(key: string) {
    super(`stream request cancelled: ${key}`);
    this.name = 'StreamCancelled';
  }
}

export interface SchedulerOptions {
  /**
   * Global in-flight cap. Browsers give ~6 connections per origin over HTTP/1.1
   * and multiplex over HTTP/2 but still queue; 6 keeps the pipe full without
   * making cancellation meaningless because everything already started.
   */
  maxConcurrent?: number;
}

export class DataStreamManager {
  private readonly maxConcurrent: number;
  private queue: Entry[] = [];
  private active = new Map<string, Entry>();
  /** Deduplication: key -> the promise every caller for that key shares. */
  private inflight = new Map<string, Promise<unknown>>();
  private seq = 0;

  constructor(options: SchedulerOptions = {}) {
    this.maxConcurrent = Math.max(1, options.maxConcurrent ?? 6);
  }

  /**
   * Queue a request, or join the one already running for the same key.
   *
   * Re-requesting a QUEUED key at a higher priority promotes it rather than
   * duplicating it — which is what happens constantly in practice: a tile
   * queued as `prefetch` becomes `visible` the moment the camera moves.
   */
  request<T>(req: StreamRequest<T>): Promise<T> {
    const existing = this.inflight.get(req.key);
    if (existing) {
      this.promote(req.key, req.priority);
      return existing as Promise<T>;
    }
    let resolve!: (v: unknown) => void;
    let reject!: (e: unknown) => void;
    const promise = new Promise<unknown>((res, rej) => {
      resolve = res;
      reject = rej;
    });
    this.inflight.set(req.key, promise);
    this.queue.push({
      key: req.key,
      priority: req.priority,
      group: req.group,
      run: req.run as (s: AbortSignal) => Promise<unknown>,
      resolve,
      reject,
      seq: this.seq++,
    });
    this.pump();
    return promise as Promise<T>;
  }

  /** Raise a queued request's priority. Never lowers it. */
  promote(key: string, priority: Priority): void {
    const entry = this.queue.find((e) => e.key === key);
    if (entry && RANK[priority] < RANK[entry.priority])
      entry.priority = priority;
  }

  /**
   * Cancel by key. Aborts the fetch if it has started, drops it if not.
   *
   * Dropping a queued entry matters as much as aborting a live one: under a
   * concurrency cap most of a pan's requests never started, and leaving them
   * queued means the next frame's genuinely-visible work waits behind data
   * nobody wants.
   */
  cancel(key: string): boolean {
    const queuedAt = this.queue.findIndex((e) => e.key === key);
    if (queuedAt >= 0) {
      const [entry] = this.queue.splice(queuedAt, 1);
      this.settleCancelled(entry);
      return true;
    }
    const running = this.active.get(key);
    if (running) {
      running.controller?.abort();
      return true;
    }
    return false;
  }

  /** Cancel everything matching a predicate — the pan-away case. */
  cancelWhere(
    predicate: (r: {
      key: string;
      group?: string;
      priority: Priority;
    }) => boolean,
  ): number {
    const keys = [
      ...this.queue.map((e) => ({
        key: e.key,
        group: e.group,
        priority: e.priority,
      })),
      ...[...this.active.values()].map((e) => ({
        key: e.key,
        group: e.group,
        priority: e.priority,
      })),
    ]
      .filter(predicate)
      .map((r) => r.key);
    let n = 0;
    for (const k of keys) if (this.cancel(k)) n++;
    return n;
  }

  stats() {
    return {
      active: this.active.size,
      queued: this.queue.length,
      maxConcurrent: this.maxConcurrent,
    };
  }

  private settleCancelled(entry: Entry) {
    this.inflight.delete(entry.key);
    entry.reject(new StreamCancelled(entry.key));
  }

  /** Start as much queued work as the budget allows, most urgent first. */
  private pump(): void {
    while (this.active.size < this.maxConcurrent && this.queue.length) {
      // Sort at pump time rather than on insert: priorities are promoted while
      // entries sit in the queue, so insertion order is not final order.
      this.queue.sort(
        (a, b) => RANK[a.priority] - RANK[b.priority] || a.seq - b.seq,
      );
      const entry = this.queue.shift() as Entry;
      this.start(entry);
    }
  }

  private start(entry: Entry): void {
    const controller = new AbortController();
    entry.controller = controller;
    this.active.set(entry.key, entry);
    let settled = false;
    const finish = (fn: () => void) => {
      if (settled) return;
      settled = true;
      this.active.delete(entry.key);
      this.inflight.delete(entry.key);
      fn();
      this.pump();
    };
    // An abort must settle the caller even if `run` ignores its signal —
    // otherwise a badly-behaved fetch holds a slot and a promise forever.
    controller.signal.addEventListener('abort', () =>
      finish(() => entry.reject(new StreamCancelled(entry.key))),
    );
    entry
      .run(controller.signal)
      .then((v) => finish(() => entry.resolve(v)))
      .catch((e) => finish(() => entry.reject(e)));
  }
}
